import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import {
  AccordionMultiSelect,
  ActionModal,
  Alert,
  Button,
  Checkbox,
  Flex,
  Icon,
  Modal,
  Select,
  Text,
  useToast,
} from '@percent/lemonade'

import { useFilters } from '../../hooks/useFilters'
import { useInsightsDataset } from '../../hooks/useInsightsDataset'
import {
  INSIGHTS_EXPORT_DATE_RANGE_TOO_LARGE,
  INSIGHTS_EXPORT_TOO_LARGE,
  InsightsExportError,
  useInsightsDatasetExport,
} from '../../hooks/useInsightsDatasetExport'
import {
  DynamicColumnsOption,
  DynamicColumnsPart,
  filterOptionValueLabel,
  FilterValues,
  ResolvedFilter,
} from '../../types/insights.types'
import { DatasetColumnConfig } from '../InsightsDataset/InsightsDataset'

export type InsightsExportLabels = {
  /** Header trigger button text, e.g. "CSV". */
  trigger: string
  /** Dialog title, e.g. "Export donations by employee". */
  title: string
  /** Sub-line under the title (the export uses the report's current filters). */
  description?: string
  appliedFiltersHeading: string
  /** Shown when no user filters are active. */
  noFiltersApplied: string
  columnsLabel: string
  modeDefault: (count: number) => string
  modeAll: (count: number) => string
  modeCustom: string
  /** Heading over the list of included columns shown for the Default / All modes, e.g. "Includes:". */
  includes: string
  zeroColumnsError: string
  cancel: string
  export: string
  exportSuccess: (rowCount: number) => string
  /** Generic export failure (fallback for any unrecognised error). */
  exportError: string
  /** The filtered result set exceeds the export row cap (NEX-714). */
  exportTooLarge: string
  /** The selected date range exceeds the export span cap (NEX-714). */
  exportDateRangeTooLarge: string
  /** Shown when the backend export endpoint isn't wired yet (NEX-712 PR 2). */
  exportUnavailable: string
  /** Single dismiss button shown in the empty state (no Export). */
  close: string
  /** Empty-state heading — also the toast if an export comes back with 0 rows. */
  emptyTitle: string
  /** Empty-state body when active filters exclude every row. */
  emptyFilteredDescription: string
  /** Empty-state body when the dataset has no data at all. */
  emptyNoDataDescription: string
}

export type InsightsExportProps = {
  /** Dataset slug (e.g. `donations`) — matches the report's `Insights.Dataset`. */
  dataset: string
  /** The report's Default column set (the same config the table renders). */
  defaultColumns: DatasetColumnConfig[]
  /**
   * Column ids the CSV never offers, in any mode. The dataset response drives
   * "All columns" and the Custom grid, so a column the report should not export
   * has to be dropped here rather than merely left out of `defaultColumns`.
   */
  excludeColumns?: string[]
  /** Offers a checkbox that adds every dynamic column of the dataset to the CSV. */
  dynamicColumns?: DynamicColumnsOption
  /** Download filename, e.g. `donations-by-employee.csv`. */
  fileName: string
  labels: InsightsExportLabels
  testId?: string
}

type ColumnMode = 'default' | 'all' | 'custom'

const partItemValue = (part: DynamicColumnsPart, itemId: string) =>
  `${part.id}:${itemId}`

// The shortest request for the ticked items: the bare group when all are ticked,
// `<group>:<part>` for a fully ticked part, else one `<group>:<part>:<item>` each.
export const toColumnGroupSelections = (
  group: string,
  parts: DynamicColumnsPart[],
  selected: string[]
): string[] => {
  const ticked = new Set(selected)
  const perPart = parts.map((part) => {
    const values = part.items.map((item) => partItemValue(part, item.id))

    return {
      part,
      picked: values.filter((value) => ticked.has(value)),
      total: values.length,
    }
  })

  if (perPart.every(({ picked, total }) => picked.length === total)) {
    return [group]
  }

  return perPart.flatMap(({ part, picked, total }) =>
    picked.length === total
      ? [`${group}:${part.id}`]
      : picked.map((value) => `${group}:${value}`)
  )
}

// macOS hides overlay scrollbars until you scroll, so the list keeps a styled one visible.
const IncludedColumns = styled.ul`
  ${({ theme }) => `
    margin: 0;
    padding: ${theme.sizes.xs} ${theme.sizes.xs} ${theme.sizes.xs} 28px;
    max-height: 200px;
    overflow-y: scroll;
    border: 1px solid ${theme.colors.gray80};
    border-radius: ${theme.borderRadii.medium};
    scrollbar-width: thin;
    scrollbar-color: ${theme.colors.gray300} transparent;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.gray300};
      border-radius: 4px;
    }
  `}
`

/** One row in the read-only "Applied filters" summary. */
type AppliedFilter = { key: string; label: string; value: string }

const formatDate = (iso: string): string => {
  const date = dayjs(iso)

  return date.isValid() ? date.format('D MMM YYYY') : iso
}

/**
 * Summarise the report's currently-applied filters for the read-only dialog
 * section. Locked filters (hidden domain scope) are skipped; date range is read
 * from its `period` / `from` / `to` backing keys; other values are resolved to
 * their display labels via the filter definition's options.
 */
const describeAppliedFilters = (
  filterDefs: ResolvedFilter[],
  values: FilterValues,
  lockedFilters: FilterValues
): AppliedFilter[] => {
  const lockedKeys = new Set(Object.keys(lockedFilters))

  return filterDefs.flatMap((def): AppliedFilter[] => {
    if (lockedKeys.has(def.key)) return []

    if (def.valueType === 'dateRange') {
      const period = values.period
      if (typeof period === 'string' && period.length > 0) {
        const presetLabel =
          def.presets?.find((preset) => preset.key === period)?.label ?? period

        return [{ key: def.key, label: def.label, value: presetLabel }]
      }

      const from = values.from
      const to = values.to
      if (typeof from === 'string' && typeof to === 'string') {
        return [
          {
            key: def.key,
            label: def.label,
            value: `${formatDate(from)} – ${formatDate(to)}`,
          },
        ]
      }

      return []
    }

    const raw = values[def.key]
    if (raw === undefined || (Array.isArray(raw) && raw.length === 0)) return []

    const labelByValue = new Map(
      (def.values ?? []).map((option) => {
        const { value, label } = filterOptionValueLabel(option)

        return [value, label]
      })
    )
    const display = (Array.isArray(raw) ? raw : [raw])
      .map((value) => labelByValue.get(value) ?? value)
      .join(', ')

    return [{ key: def.key, label: def.label, value: display }]
  })
}

/** API error code → the label that explains it (NEX-714 export size guards). */
const EXPORT_ERROR_LABEL: Record<
  string,
  'exportTooLarge' | 'exportDateRangeTooLarge'
> = {
  [INSIGHTS_EXPORT_TOO_LARGE]: 'exportTooLarge',
  [INSIGHTS_EXPORT_DATE_RANGE_TOO_LARGE]: 'exportDateRangeTooLarge',
}

/**
 * Pick the toast message for a failed export: a specific, actionable line for the
 * size-guard errors (NEX-714), the generic fallback for anything else.
 */
const exportErrorMessage = (
  error: unknown,
  labels: InsightsExportLabels
): string => {
  const code = error instanceof InsightsExportError ? error.code : undefined
  const labelKey = code ? EXPORT_ERROR_LABEL[code] : undefined

  return labelKey ? labels[labelKey] : labels.exportError
}

/**
 * The export dialog body — only mounted while open, so each open starts fresh
 * (column mode resets to Default and the applied-filters summary re-syncs to the
 * report's current filters, per the spec). Fetches the dataset's full column
 * metadata (no projection) to populate "All columns" and the Custom grid.
 */
function ExportDialog({
  dataset,
  defaultColumns,
  excludeColumns,
  dynamicColumns,
  fileName,
  labels,
  testId,
  onClose,
}: Readonly<
  Omit<InsightsExportProps, 'testId'> & { testId?: string; onClose: () => void }
>) {
  const { addToast } = useToast()
  const { values, filterDefs, lockedFilters, hasActiveFilters } = useFilters()
  const {
    columns: responseColumns,
    totalResults,
    isLoading,
    error,
  } = useInsightsDataset(dataset, {
    filters: values,
    pageSize: 1,
  })
  const { exportCsv, isExporting, canExport } =
    useInsightsDatasetExport(dataset)

  const excluded = useMemo(
    () => new Set(excludeColumns ?? []),
    [excludeColumns]
  )
  const allColumns = useMemo(
    () => responseColumns.filter((column) => !excluded.has(column.key)),
    [responseColumns, excluded]
  )
  const defaultIds = useMemo(
    () =>
      defaultColumns
        .map((column) => column.id)
        .filter((id) => !excluded.has(id)),
    [defaultColumns, excluded]
  )
  const allIds = useMemo(
    () => allColumns.map((column) => column.key),
    [allColumns]
  )

  const [mode, setMode] = useState<ColumnMode>('default')
  const [customSelected, setCustomSelected] = useState<Set<string>>(
    () => new Set(defaultIds)
  )

  // Header label for a column id: a Default config header override wins, else the
  // response column's own label, else the raw id.
  const headerFor = (id: string): string =>
    defaultColumns.find((column) => column.id === id)?.header ??
    allColumns.find((column) => column.key === id)?.label ??
    id

  // Selected columns in canonical (response) order. Custom keeps only ticked ones.
  const canonicalOrder = allIds.length > 0 ? allIds : defaultIds
  const selectedIds =
    mode === 'default'
      ? defaultIds
      : mode === 'all'
      ? allIds
      : canonicalOrder.filter((id) => customSelected.has(id))

  const hasZeroColumns = mode === 'custom' && customSelected.size === 0

  const [includeDynamicColumns, setIncludeDynamicColumns] = useState(false)
  const dynamicColumnsUnavailable =
    includeDynamicColumns && !dynamicColumns?.isAvailable(values)

  const { data: parts, isLoading: partsLoading } = useQuery({
    queryKey: [
      'insightsExportDynamicColumnParts',
      dataset,
      dynamicColumns?.group,
      values,
    ],
    queryFn: () => dynamicColumns?.parts?.load(values) ?? [],
    enabled: Boolean(
      dynamicColumns?.parts && dynamicColumns.isAvailable(values)
    ),
  })
  const pickParts = Boolean(dynamicColumns?.parts && parts && parts.length > 0)
  const [selectedParts, setSelectedParts] = useState<string[]>([])
  const partOptions = useMemo(
    () =>
      Object.fromEntries(
        (parts ?? []).map((part) => [
          part.label,
          part.items.map((item) => ({
            value: partItemValue(part, item.id),
            label: item.label,
          })),
        ])
      ),
    [parts]
  )

  const dynamicColumnGroups = (): string[] | undefined => {
    if (!dynamicColumns) return undefined
    if (pickParts) {
      return selectedParts.length > 0
        ? toColumnGroupSelections(
            dynamicColumns.group,
            parts ?? [],
            selectedParts
          )
        : undefined
    }

    return includeDynamicColumns ? [dynamicColumns.group] : undefined
  }

  const toggleCustom = (id: string) =>
    setCustomSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }

      return next
    })

  const appliedFilters = useMemo(
    () => describeAppliedFilters(filterDefs, values, lockedFilters),
    [filterDefs, values, lockedFilters]
  )

  // No rows for the current filters (or the dataset is empty) → show an empty
  // state instead of letting the user download a header-only CSV. Two messages:
  // active filters excluded everything vs. the dataset has no data at all.
  const isEmpty = !isLoading && !error && totalResults === 0
  const emptyDescription = hasActiveFilters
    ? labels.emptyFilteredDescription
    : labels.emptyNoDataDescription

  // lemonade's Select re-syncs its displayed value whenever `defaultValue`
  // changes identity, so a fresh object each render would snap the selection
  // back to "Default" on every re-render (picking a mode, columns loading).
  // Keep a stable reference, memoised on the (constant) default-label string.
  const defaultModeLabel = labels.modeDefault(defaultIds.length)
  const defaultModeOption = useMemo(
    () => ({ value: 'default', label: defaultModeLabel }),
    [defaultModeLabel]
  )
  const modeOptions = [
    defaultModeOption,
    { value: 'all', label: labels.modeAll(allIds.length) },
    { value: 'custom', label: labels.modeCustom },
  ]

  const handleExport = async () => {
    try {
      const rowCount = await exportCsv({
        columns: selectedIds,
        columnGroups: dynamicColumnGroups(),
        fileName,
      })
      // Belt-and-suspenders: the export came back empty despite the pre-export
      // check — surface "no data" rather than a header-only download (the hook
      // already skipped the download in this case).
      if (rowCount === 0) {
        addToast(labels.emptyTitle, 'error')
        onClose()

        return
      }
      addToast(labels.exportSuccess(rowCount), 'success')
      onClose()
    } catch (error) {
      addToast(exportErrorMessage(error, labels), 'error')
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      data-testid={testId}
      showModalOverflow={false}
    >
      <ActionModal
        title={labels.title}
        primaryButtonText={isEmpty ? labels.close : labels.export}
        secondaryButtonText={isEmpty ? undefined : labels.cancel}
        primaryBtnTestId={testId ? `${testId}-confirm` : undefined}
        secondaryBtnTestId={testId ? `${testId}-cancel` : undefined}
        handleSubmit={isEmpty ? onClose : handleExport}
        handleClose={onClose}
        loading={isEmpty ? false : isExporting}
        disabled={
          isEmpty
            ? false
            : hasZeroColumns ||
              dynamicColumnsUnavailable ||
              isLoading ||
              !canExport
        }
      >
        <Flex direction="column" gap={16}>
          {isEmpty ? (
            <Flex
              direction="column"
              align="center"
              gap={8}
              style={{ textAlign: 'center', padding: '24px 0' }}
            >
              <Icon name="empty" size={6} color="goodstack" />
              <Text fontWeight="medium">{labels.emptyTitle}</Text>
              <Text size="small">{emptyDescription}</Text>
            </Flex>
          ) : (
            <>
              {labels.description && (
                <Text size="small">{labels.description}</Text>
              )}

              <Flex direction="column" gap={4}>
                <Text size="small" fontWeight="medium">
                  {labels.appliedFiltersHeading}
                </Text>
                {appliedFilters.length > 0 ? (
                  appliedFilters.map((filter) => (
                    <Text key={filter.key} size="small">
                      {filter.label}: {filter.value}
                    </Text>
                  ))
                ) : (
                  <Text size="small">{labels.noFiltersApplied}</Text>
                )}
              </Flex>

              <Flex direction="column" gap={8}>
                <Text size="small" fontWeight="medium">
                  {labels.columnsLabel}
                </Text>
                <Select
                  name="insights-export-columns-mode"
                  data-testid={testId ? `${testId}-mode` : undefined}
                  options={modeOptions}
                  defaultValue={defaultModeOption}
                  onChange={(option) => setMode(option.value as ColumnMode)}
                />

                {mode === 'custom' ? (
                  <Flex direction="row" wrap="wrap" gap={12}>
                    {allColumns.map((column) => (
                      <Checkbox
                        key={column.key}
                        name={`insights-export-col-${column.key}`}
                        label={headerFor(column.key)}
                        variant="default"
                        active={customSelected.has(column.key)}
                        onChange={() => toggleCustom(column.key)}
                        dataTestId={
                          testId ? `${testId}-col-${column.key}` : undefined
                        }
                      />
                    ))}
                  </Flex>
                ) : (
                  <Flex direction="column" gap={4}>
                    <Text size="small">{labels.includes}</Text>
                    <IncludedColumns
                      data-testid={
                        testId ? `${testId}-included-columns` : undefined
                      }
                    >
                      {selectedIds.map((id) => (
                        <li key={id}>
                          <Text size="small">{headerFor(id)}</Text>
                        </li>
                      ))}
                    </IncludedColumns>
                  </Flex>
                )}

                {dynamicColumns?.parts && pickParts && (
                  <Flex direction="column" gap={4}>
                    <Text size="small" fontWeight="medium">
                      {dynamicColumns.parts.label}
                    </Text>
                    <AccordionMultiSelect
                      data={partOptions}
                      value={selectedParts}
                      onChange={setSelectedParts}
                      selectAllLabel={dynamicColumns.parts.selectAllLabel}
                      data-testid={
                        testId ? `${testId}-dynamic-column-parts` : undefined
                      }
                    />
                  </Flex>
                )}
                {dynamicColumns && !pickParts && !partsLoading && (
                  <Checkbox
                    name="insights-export-dynamic-columns"
                    label={dynamicColumns.label}
                    variant="default"
                    active={includeDynamicColumns}
                    onChange={() => setIncludeDynamicColumns((prev) => !prev)}
                    dataTestId={
                      testId ? `${testId}-dynamic-columns` : undefined
                    }
                  />
                )}
                {dynamicColumnsUnavailable && (
                  <Alert variant="error">{dynamicColumns?.unavailable}</Alert>
                )}
                {hasZeroColumns && (
                  <Alert variant="error">{labels.zeroColumnsError}</Alert>
                )}
                {!canExport && (
                  <Alert variant="info">{labels.exportUnavailable}</Alert>
                )}
              </Flex>
            </>
          )}
        </Flex>
      </ActionModal>
    </Modal>
  )
}

/**
 * Report-header CSV export. Renders a trigger button; the dialog (column picker +
 * applied-filters summary) mounts on open and reads the live filters from the
 * surrounding `<Insights.Domain>`. Drop it into a report header beside
 * `<Insights.Filters>`.
 */
export function InsightsExport({
  dataset,
  defaultColumns,
  excludeColumns,
  dynamicColumns,
  fileName,
  labels,
  testId,
}: Readonly<InsightsExportProps>) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        variant="secondary"
        size="small"
        startIcon="download"
        onClick={() => setOpen(true)}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        {labels.trigger}
      </Button>
      {open && (
        <ExportDialog
          dataset={dataset}
          defaultColumns={defaultColumns}
          excludeColumns={excludeColumns}
          dynamicColumns={dynamicColumns}
          fileName={fileName}
          labels={labels}
          testId={testId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}
